import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import api from '../services/api';

const useAuthStore = create(
    devtools(
        persist(
            (set, get) => ({
                // State
                user: null,
                admin: null,
                token: null,
                userType: null, // 'user' or 'admin'
                isAuthenticated: false,
                isLoading: true,
                mustChangePassword: false,

                // Actions
                setAuth: (data) => {
                    const { token, user, admin, userType, mustChangePassword } = data;

                    // Set token in axios defaults
                    if (token) {
                        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    }

                    set({
                        token,
                        user: user || null,
                        admin: admin || null,
                        userType,
                        isAuthenticated: true,
                        isLoading: false,
                        mustChangePassword: mustChangePassword || false,
                    });
                },

                clearAuth: () => {
                    // Remove token from axios defaults
                    delete api.defaults.headers.common['Authorization'];

                    set({
                        user: null,
                        admin: null,
                        token: null,
                        userType: null,
                        isAuthenticated: false,
                        isLoading: false,
                        mustChangePassword: false,
                    });
                },

                updateUser: (userData) => {
                    set((state) => ({
                        user: { ...state.user, ...userData },
                    }));
                },

                updateAdmin: (adminData) => {
                    set((state) => ({
                        admin: { ...state.admin, ...adminData },
                    }));
                },

                checkAuth: async () => {
                    const state = get();

                    if (!state.token) {
                        set({ isLoading: false });
                        return;
                    }

                    try {
                        // Set token in headers
                        api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;

                        // Verify token based on user type
                        if (state.userType === 'admin') {
                            const response = await api.get('/auth/admin/verify');
                            set({
                                admin: response.data.admin,
                                isAuthenticated: true,
                                isLoading: false,
                            });
                        } else {
                            const response = await api.get('/auth/verify');
                            set({
                                user: response.data.user,
                                isAuthenticated: true,
                                isLoading: false,
                                mustChangePassword: response.data.user.mustChangePassword,
                            });
                        }
                    } catch (error) {
                        // Token is invalid
                        get().clearAuth();
                    }
                },

                // User login
                login: async (email, password, totpToken = null) => {
                    try {
                        const response = await api.post('/auth/login', {
                            email,
                            password,
                            totpToken,
                        });

                        const { token, user, mustChangePassword, requiresTwoFactor } = response.data;

                        if (requiresTwoFactor) {
                            return { requiresTwoFactor: true };
                        }

                        get().setAuth({
                            token,
                            user,
                            userType: 'user',
                            mustChangePassword,
                        });

                        return { success: true, mustChangePassword };
                    } catch (error) {
                        throw error;
                    }
                },

                // Admin login
                adminLogin: async (email, password, totpToken = null) => {
                    try {
                        const response = await api.post('/auth/admin/login', {
                            email,
                            password,
                            totpToken,
                        });

                        const { token, admin, requiresTwoFactor } = response.data;

                        if (requiresTwoFactor) {
                            return { requiresTwoFactor: true };
                        }

                        get().setAuth({
                            token,
                            admin,
                            userType: 'admin',
                        });

                        return { success: true };
                    } catch (error) {
                        throw error;
                    }
                },

                // Logout
                logout: async () => {
                    const state = get();

                    try {
                        if (state.userType === 'admin' && state.token) {
                            await api.post('/auth/admin/logout');
                        }
                    } catch (error) {
                        console.error('Logout error:', error);
                    } finally {
                        get().clearAuth();
                    }
                },

                // Change password
                changePassword: async (currentPassword, newPassword) => {
                    try {
                        await api.post('/auth/change-password', {
                            currentPassword,
                            newPassword,
                        });

                        set({ mustChangePassword: false });
                        return { success: true };
                    } catch (error) {
                        throw error;
                    }
                },

                // Request password reset
                requestPasswordReset: async (email) => {
                    try {
                        await api.post('/auth/forgot-password', { email });
                        return { success: true };
                    } catch (error) {
                        throw error;
                    }
                },

                // Reset password
                resetPassword: async (token, newPassword) => {
                    try {
                        await api.post('/auth/reset-password', {
                            token,
                            newPassword,
                        });
                        return { success: true };
                    } catch (error) {
                        throw error;
                    }
                },
            }),
            {
                name: 'auth-storage',
                partialize: (state) => ({
                    token: state.token,
                    userType: state.userType,
                }),
            }
        )
    )
);

export { useAuthStore };